import * as React from 'react';

import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { ContainerSizeSelector } from 'components/tailwind-ui/Selectors/ContainerSizeSelector';
import { ShippingLineSelector } from 'components/tailwind-ui/Selectors/ShippingLineSelector';
import { ReactComponent as VesselIcon } from 'assets/andora-icons/vessel.svg';
import { ReactComponent as ContainerIcon } from 'assets/andora-icons/container.svg';
import { useRegion } from 'common/hooks/region';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TextField } from '@mui/material';
import moment, { Moment } from 'moment';
import { ERL_STOP_THE_CLOCK_PARAMS } from 'pages/T_Appointments/params';
import { ContainerType, ShippingLine } from 'types/Shipping';
import { useUserContext } from 'common/contexts/UserContext';
import { SegmentService } from 'services/helpers/segment';
import { NextAppointmentsService } from '../NextAppointments/services/next-appointments.service';

const PREFERRED_DEFAULT_SSL: ShippingLine['key'] = 'CMA';
const PREFERRED_DEFAULT_CONTAINER_TYPE: ContainerType['key'] = "20'";

export const FilterControls: React.FC = () => {
  const region = useRegion();
  const { accessHistoricalErlTable } = useFlags();
  const { searchParams, setSearchParams } = useCustomSearchParams(ERL_STOP_THE_CLOCK_PARAMS);

  // Some logic for setting the initial values of the selectors
  const [initialValues, setInitialValues] = React.useState<{
    shippingLines?: ShippingLine[];
    containerTypes?: ContainerType[];
  }>({});
  React.useEffect(() => {
    if (initialValues.containerTypes && initialValues.shippingLines) {
      const defaultShippingLine =
        initialValues.shippingLines.find((s) => s.key === PREFERRED_DEFAULT_SSL) ||
        initialValues.shippingLines[0];

      const defaultContainerType =
        initialValues.containerTypes.find((c) => c.key === PREFERRED_DEFAULT_CONTAINER_TYPE) ||
        initialValues.containerTypes[0];

      if (searchParams.containerType && searchParams.shippingLine) {
        if (
          !initialValues.shippingLines.find((s) => s.key === searchParams.shippingLine) ||
          !initialValues.containerTypes.find((c) => c.key === searchParams.containerType)
        ) {
          // When either of the chosen shipping line or container type is not in the list of available options, reset the search params
          setSearchParams({
            shippingLine: defaultShippingLine?.key,
            containerType: defaultContainerType?.key,
          });
        }
      } else {
        // When either the shipping line or container type is not defined in the search params, reset the search params while preserving any set values
        setSearchParams({
          shippingLine: searchParams.shippingLine || defaultShippingLine?.key,
          containerType: searchParams.containerType || defaultContainerType?.key,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, setInitialValues, setSearchParams]);

  const { user } = useUserContext();
  const track = React.useCallback(
    (shippingLine: string, containerType: string) => {
      NextAppointmentsService.fireGAsearchEvent(user, shippingLine, containerType);
      SegmentService.trackEventNoSlack('Empty-appointment Searched', {
        shippingLine,
        containerType,
        email: user?.email,
      });
    },
    [user]
  );

  return (
    <div className="flex-grow space-y-4">
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
        <ShippingLineSelector
          id="shippingLines"
          value={searchParams.shippingLine}
          onChange={(shippingLine) => {
            setSearchParams({ shippingLine });
            track(shippingLine, searchParams.containerType);
          }}
          shippingLineFilters={{ portCollectionCodes: [region.code] }}
          label={
            <div className="flex items-center gap-2">
              <VesselIcon />
              <span>Select a shipping line</span>
            </div>
          }
          className="flex-grow"
          onSuccess={(shippingLines) => {
            // Automatically set default shipping line
            setInitialValues((s) => ({ ...s, shippingLines }));
          }}
        />
        <ContainerSizeSelector
          id="containerTypes"
          value={searchParams.containerType}
          onChange={(containerType) => {
            setSearchParams({ containerType });
            track(searchParams.shippingLine, containerType);
          }}
          label={
            <div className="flex items-center gap-2">
              <ContainerIcon />
              <span>Select a container type</span>
            </div>
          }
          className="flex-grow"
          onSuccess={(containerTypes) => {
            // Automatically set default container type
            setInitialValues((s) => ({ ...s, containerTypes }));
          }}
        />
      </div>
      {accessHistoricalErlTable && (
        <div className="flex gap-4" id="historical-date-filters">
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              renderInput={(textFieldProps) => <TextField {...textFieldProps} fullWidth />}
              value={searchParams.forDatetime}
              onChange={(newValue) => {
                if (newValue === null || newValue?.isValid()) {
                  setSearchParams({ forDatetime: newValue });
                }
              }}
            />
          </LocalizationProvider>
          <input
            id="forDatetime"
            value={(searchParams.forDatetime as Moment | null)?.format('YYYY-MM-DD HH:mm')}
            onChange={(e) => {
              const newDate = e.target.value ? moment(e.target.value, 'YYYY-MM-DD HH:mm') : null;
              if (newDate === null || newDate?.isValid()) {
                setSearchParams({ forDatetime: newDate });
              }
            }}
          />
        </div>
      )}
    </div>
  );
};
